import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Step, CallBackProps, STATUS } from 'react-joyride';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { planType } from '../types/organization';
import { useUsers } from './user/useUsers';
import { useUserActions } from './user/useUserActions';
import { User } from '@sentry/react';

const TOUR_STEPS = {
  [planType.RAG]: [
    {
      target: 'body',
      content: 'Hey! Ready to explore how things work?',
      placement: 'center',
      disableBeacon: true,
      disableOverlayClose: true,
      hideCloseButton: true,
      locale: {
        next: 'Start the Tour',
        skip: 'Skip the tour',
      },
    },
    {
      target: '.knowledge-base-tour',
      content:
        'Add company profiles, FAQs, or reference documents (PDF/text) to train your AI chat—the more information you provide, the better its responses.',
      placement: 'bottom',
      placementBeacon: 'top',
      showProgress: true,
    },
    {
      target: '.snippet-tour',
      content: `🎉 You're all set! Copy and paste the code snippet into your website to launch your smart chatbot.`,
      placement: 'right',
    },
  ],
  default: [
    {
      target: 'body',
      content: 'Hey! Ready to explore how things work?',
      placement: 'center',
      disableBeacon: true,
      disableOverlayClose: true,
      hideCloseButton: true,
      locale: {
        next: 'Start the Tour',
        skip: 'Skip the tour',
      },
    },
    {
      target: '.knowledge-base-tour',
      content:
        'Add company profiles, FAQs, or reference documents (PDF/text) to train your AI chat—the more information you provide, the better its responses.',
      placement: 'bottom',
      placementBeacon: 'top',
      showProgress: true,
    },
    {
      target: '.chat-settings-tour',
      content:
        'Under Chat Settings → Add sample prompts in the chat description along with pre-filled suggestions to guide users on what questions they can ask.',
      placement: 'top',
      showProgress: true,
    },
    {
      target: '.snippet-tour',
      content: `🎉 You're all set! Copy and paste the code snippet into your website to launch your smart chatbot.`,
      placement: 'right',
    },
  ],
};

const PATHS = {
  bookingEnabled: [
    '/dashboard',
    '/dashboard/knowledge-base',
    '/dashboard/settings/chat-settings',
    '/dashboard/settings/integrations',
  ],
  notBookingEnabled: [
    '/dashboard',
    '/dashboard/knowledge-base',
    '/dashboard/settings/integrations',
  ],
};

const useOnboardingTour = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const authUser = useSelector((state: RootState) => state.auth.user);
  const { users, isLoading: isLoadingUsers, refetch: refetchUsers } = useUsers();
  const { updateUser } = useUserActions();

  const isBookingEnabled = selectedOrganization?.isBookingEnabled ?? false;
  const planTypeOrg = selectedOrganization?.planType;

  const [steps] = useState<Step[]>(TOUR_STEPS[planTypeOrg] || TOUR_STEPS.default);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (authUser?.email && users && users.length > 0) {
      const foundUser = users.find(
        user => user.email.toLowerCase() === authUser.email.toLowerCase()
      );
      setCurrentUser(foundUser || null);
    } else {
      setCurrentUser(null);
    }
  }, [authUser, users]);

  useEffect(() => {
    if (!isLoadingUsers && currentUser) {
      // Only run tour if hasSeenTour is explicitly false (not undefined/null)
      if (currentUser.hasSeenTour === false) {
        setRun(true);
      } else {
        setRun(false);
      }
    }
  }, [currentUser, isLoadingUsers]);

  useEffect(() => {
    if (currentUser?.hasSeenTour === false && steps[stepIndex] && stepIndex !== 0) {
      const checkElement = () => {
        const step = steps[stepIndex];
        const el =
          typeof step.target === 'string'
            ? (document.querySelector(step.target) as HTMLElement | null)
            : step.target;
        setRun(!!el);
      };

      const intervalId = setInterval(checkElement, 300);
      return () => clearInterval(intervalId);
    }
  }, [location.pathname, stepIndex, steps, currentUser]);

  const onElementLoad = (selector: string | HTMLElement, callback: () => void) => {
    const interval = setInterval(() => {
      const el =
        typeof selector === 'string'
          ? (document.querySelector(selector) as HTMLElement | null)
          : selector;

      if (el && el.offsetParent !== null) {
        clearInterval(interval);
        callback();
      }
    }, 300);
  };

  const navigateToStep = useCallback(
    (nextIndex: number) => {
      const paths = isBookingEnabled ? PATHS.bookingEnabled : PATHS.notBookingEnabled;
      const targetPath = paths[nextIndex] || '/dashboard';
      navigate(targetPath);

      const selector = steps[nextIndex]?.target;
      if (selector) {
        onElementLoad(selector, () => {
          setStepIndex(nextIndex);
          setRun(true);
        });
      } else {
        setStepIndex(nextIndex);
        setRun(true);
      }
    },
    [navigate, isBookingEnabled, steps]
  );

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (type === 'tour:end' && (status === STATUS.FINISHED || status === STATUS.SKIPPED)) {
      if (currentUser) {
        try {
          await updateUser(currentUser.cognitoId, {
            hasSeenTour: true,
            status: currentUser.status,
            role: currentUser.role,
            cognitoId: currentUser.cognitoId,
            email: currentUser.email,
            firstName: currentUser.firstName,
            mobileNo: currentUser.mobileNo || null,
            lastName: currentUser.lastName || '',
          });
          // Refetch users to sync the updated hasSeenTour value
          await refetchUsers();
          // Update local state to reflect the change
          setCurrentUser(prev => (prev ? { ...prev, hasSeenTour: true } : null));
        } catch (error) {
          console.error('Failed to update user tour status:', error);
        }
      }
      setRun(false);
      navigate('/dashboard');
      return;
    }

    if (type === 'step:after') {
      if (action === 'next') {
        setRun(false);
        navigateToStep(index + 1);
      } else if (action === 'prev') {
        setRun(false);
        if (index - 1 === 0) {
          setStepIndex(0);
          setTimeout(() => setRun(true), 300);
          navigate('/dashboard');
        } else {
          navigateToStep(index - 1);
        }
      }
    }
  };

  return {
    steps,
    run,
    setRun,
    stepIndex,
    setStepIndex,
    handleJoyrideCallback,
    currentUser,
  };
};

export default useOnboardingTour;
